import { DownloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useGetRazorpayOrdersQuery } from "../../../api/webshop/rzp/RazorpayOrdersApi";
import {
  formatRevenueOrderDate,
  quarterKey,
  quarterSort,
} from "../revenue/revenueData";
import {
  buildSimplyWebRevenueOrders,
  SimplyWebRevenueOrder,
  summarizeSimplyWebRevenue,
} from "./revenueData";

const { Title, Text } = Typography;
const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW", "VND"]);

function formatAmount(amountMinor: number, currency: string) {
  const divisor = ZERO_DECIMAL_CURRENCIES.has(currency) ? 1 : 100;
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency,
    maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2,
  }).format(amountMinor / divisor);
}

export default function SimplyWebRevenueDashboardPage() {
  const { data, isLoading, isError } = useGetRazorpayOrdersQuery();
  const [selectedQuarter, setSelectedQuarter] = useState(quarterKey(new Date()));
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const orders = useMemo(
    () => buildSimplyWebRevenueOrders(data?.Orders ?? []),
    [data],
  );
  const quarterOptions = useMemo(() => Array.from(new Set([
    quarterKey(new Date()),
    ...orders.map(order => order.quarter),
  ])).sort(quarterSort).map(quarter => ({ label: quarter, value: quarter })), [orders]);
  const quarterOrders = useMemo(
    () => orders.filter(order => order.quarter === selectedQuarter),
    [orders, selectedQuarter],
  );
  const totals = useMemo(
    () => summarizeSimplyWebRevenue(quarterOrders),
    [quarterOrders],
  );
  const currencies = useMemo(
    () => Array.from(new Set(orders.map(order => order.currency))).sort(),
    [orders],
  );
  const visibleOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return quarterOrders.filter(order => (
      (currency === "all" || order.currency === currency)
      && order.orderId.toLowerCase().includes(normalizedSearch)
    ));
  }, [currency, quarterOrders, search]);

  const columns: ColumnsType<SimplyWebRevenueOrder> = [
    { title: "Order ID", dataIndex: "orderId" },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      render: formatRevenueOrderDate,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: "Gross Revenue",
      dataIndex: "amountMinor",
      align: "right",
      render: (value: number, row) => formatAmount(value, row.currency),
    },
    {
      title: "Status",
      key: "status",
      render: () => <Tag color="green">Paid</Tag>,
    },
  ];

  function exportOrders() {
    if (visibleOrders.length === 0) {
      message.warning("No website orders to export");
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(visibleOrders.map(order => ({
      "Order ID": order.orderId,
      "Order Date": formatRevenueOrderDate(order.orderDate),
      Currency: order.currency,
      "Gross Revenue": ZERO_DECIMAL_CURRENCIES.has(order.currency)
        ? order.amountMinor
        : order.amountMinor / 100,
      Status: "Paid",
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Website Sales");
    XLSX.writeFile(workbook, `simply-web-sales-${selectedQuarter.toLowerCase().replace(" ", "-")}.xlsx`);
    message.success(`Exported ${visibleOrders.length} website orders`);
  }

  return <Flex gap={16} vertical>
    <Flex justify="space-between" align="start" wrap="wrap" gap={8}>
      <div>
        <Title level={3} style={{ margin: 0 }}>Revenue Dashboard (SIMPLY Web)</Title>
        <Text type="secondary">Track gross revenue from SIMPLY website orders</Text>
      </div>
      <Select
        aria-label="Reporting quarter"
        value={selectedQuarter}
        options={quarterOptions}
        onChange={setSelectedQuarter}
        style={{ width: 140 }}
      />
    </Flex>

    <Alert
      type="info"
      showIcon
      message="Figures are gross amounts before Razorpay fees and deductions. Currencies are not converted."
    />
    {isError && <Alert type="error" showIcon message="Unable to load website orders" />}

    <Row gutter={[16, 16]}>
      {Object.entries(totals).map(([currencyCode, total]) => <Col xs={24} md={12} xl={8} key={currencyCode}>
        <Card>
          <Statistic
            title={`Total Sales (${currencyCode})`}
            value={formatAmount(total, currencyCode)}
          />
          <Text type="secondary">{selectedQuarter}</Text>
        </Card>
      </Col>)}
      {!isLoading && !isError && Object.keys(totals).length === 0 && <Col span={24}>
        <Card><Text type="secondary">No paid website orders for this quarter.</Text></Card>
      </Col>}
    </Row>

    <Card title="Website Orders">
      <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
        <Input.Search
          aria-label="Search order ID"
          placeholder="Search order ID"
          value={search}
          onChange={event => setSearch(event.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <Select
          aria-label="Currency"
          value={currency}
          onChange={setCurrency}
          style={{ width: 150 }}
          options={[
            { label: "All currencies", value: "all" },
            ...currencies.map(value => ({ label: value, value })),
          ]}
        />
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          disabled={visibleOrders.length === 0 || isError}
          onClick={exportOrders}
        >
          Export Orders
        </Button>
      </Flex>
      <Table
        rowKey="orderId"
        columns={columns}
        dataSource={isError ? [] : visibleOrders}
        loading={isLoading}
        pagination={{ defaultPageSize: 20, pageSizeOptions: [10, 20, 50, 100] }}
        size="small"
        bordered
      />
    </Card>
  </Flex>;
}
